<template>
  <div class="print-widget-component">
    <div class="btn-hover-times">
      <b-button
        class="btn__icon btn__icon--primary px-1 py-0"
        @click="$emit('hideItemLayout')"
      >
        <i
          class="fal fa-times"
          aria-hidden="true"
        />
      </b-button>
    </div>
    <b-container>
      <b-row>
        <b-col>
          <div class="item-text d-flex px-2 pb-2 mt-2">
            <div class="d-flex align-items-center justify-content-center">
              <i
                :class="widget.icon + ' mr-3 main main-icon'"
                aria-hidden="true"
              />
            </div>
            <div class="d-flex align-items-center">
              <span>{{ $t(widget.label) }}</span>
            </div>
          </div>
        </b-col>
      </b-row>
      <div
        v-if="!selectedTemplateId"
        class="print-widget-blank"
      >
        <div class="d-flex align-items-center justify-content-center">
          <i
            class="fal fa-list-ul"
            aria-hidden="true"
          />
          <i
            class="fas fa-plus"
            style="position:absolute; font-size: 14px; bottom: 20px; right: 20px;"
            aria-hidden="true"
          />
          <div class="circle" />
        </div>
        <span class="mt-3">{{ $t('component.printLabelsModal.blankMessage') }}</span>
      </div>
      <div v-if="selectedTemplateId">
        <b-row>
          <b-col>
            <div class="d-flex justify-content-between">
              <div>
                <div class="d-flex align-items-center">
                  <div class="widget-truncate">
                    <span class="main template-text">{{ templateForm.title }}</span>
                  </div>
                  <b-button
                    id="pop-print-template"
                    class="btn__icon btn__icon--primary  px-2"
                  >
                    <i
                      class="far fa-chevron-down"
                      aria-hidden="true"
                    />
                  </b-button>
                </div>
                <b-popover
                  target="pop-print-template"
                  placement="top"
                  triggers="click blur"
                >
                  <div class="ap-popover">
                    <div class="ap-popover__header">
                      {{ $t('component.printLabelsModal.loadTemplate') }}
                    </div>
                    <ul>
                      <li
                        v-for="template in templates"
                        :key="template.id"
                      >
                        <b-button @click="selectTemplate(template)">
                          {{ template.title }}
                        </b-button>
                      </li>
                    </ul>
                  </div>
                </b-popover>
              </div>
              <div>
                <b-button
                  v-tooltip="$t('component.printLabelsModal.editTemplate')"
                  class="btn__icon btn__icon--primary px-1"
                  @click="showPrintLabelsModal(true)"
                >
                  <i
                    class="far fa-pencil"
                    aria-hidden="true"
                  />
                </b-button>
                <b-button
                  v-tooltip="$t('component.printLabelsModal.openPrintLabelsModal')"
                  class="btn__icon btn__icon--primary px-2"
                  style="margin-right:0 !important"
                  @click="showPrintLabelsModal(false)"
                >
                  <i
                    class="far fa-external-link"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <vt-combo-select
              v-model="templateForm.template.caseType"
              v-validate="'required'"
              track-by="id"
              :options="caseTypes"
              label="descriptionInLaboratory"
              :placeholder="$t('component.printLabelsModal.caseType')"
            />
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col cols="12">
            <div class="switch-btn mt-1 p-0 d-flex align-items-center">
              <input
                id="toggleBlock"
                v-model="templateForm.template.rangeLabel"
                type="checkbox"
                class="offscreen"
                aria-hidden="true"
              >
              <label
                for="toggleBlock"
                class="switch mb-0"
              />
              <span class="ml-2 dark">{{ $t('component.printLabelsModal.useRank') }}</span>
            </div>
          </b-col>
        </b-row>
        <div class="rank-block d-flex align-items-center">
          <b-row
            v-if="!templateForm.template.rangeLabel"
            class="mt-2"
          >
            <b-col class="pr-0 d-flex align-items-center">
              <span class="next-title dark">{{ $t('component.printLabelsModal.printTheNext') }}</span>
            </b-col>
            <b-col cols="5">
              <vt-input
                v-model="templateForm.template.numberOfNextLabels"
                type="number"
                class="form-control-sm"
              />
            </b-col>
          </b-row>
          <b-row
            v-else
            class="mt-2"
          >
            <b-col class="pr-1">
              <div class="form-label">
                <label>{{ $t('component.printLabelsModal.since') }}</label>
              </div>
              <vt-input
                v-model="templateForm.template.minValue"
                class="form-control-sm"
              />
            </b-col>
            <b-col class="pl-1">
              <div class="form-label">
                <label>{{ $t('component.printLabelsModal.to') }}</label>
              </div>
              <vt-input
                v-model="templateForm.template.maxValue"
                class="form-control-sm"
              />
            </b-col>
          </b-row>
        </div>
        <b-row class="mt-3">
          <b-col>
            <span class="type-selector-text dark">{{ $t('component.printLabelsModal.selectLabelsType') }}</span>
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col>
            <div class="d-flex">
              <div
                v-for="(entityCopy, index) in templateForm.template.entityCopiesRequestList"
                :key="index"
                :class="{'active':entityCopy.checked}"
                class="print-widget-button d-flex align-items-center justify-content-center"
                @click="entitySelected(entityCopy)"
              >
                <div
                  v-if="entityCopy.entity === 'CASE'"
                  v-tooltip="$t('component.printLabelsModal.cases')"
                  class="h-100 w-100 d-flex justify-content-center align-items-center"
                >
                  <i
                    class="fal fa-chart-network"
                    aria-hidden="true"
                  />
                </div>
                <div
                  v-if="entityCopy.entity === 'SAMPLE'"
                  v-tooltip="$t('component.printLabelsModal.samples')"
                  class="h-100 w-100 d-flex justify-content-center align-items-center"
                >
                  <i
                    class="fal fa-prescription-bottle"
                    aria-hidden="true"
                  />
                </div>
                <div
                  v-if="entityCopy.entity === 'PORTION'"
                  v-tooltip="$t('component.printLabelsModal.portions')"
                  class="h-100 w-100 d-flex justify-content-center align-items-center"
                >
                  <i
                    class="icon-block"
                    aria-hidden="true"
                  />
                </div>
                <div
                  v-if="entityCopy.entity === 'SUBPORTION'"
                  v-tooltip="$t('component.printLabelsModal.subportions')"
                  class="h-100 w-100 d-flex justify-content-center align-items-center"
                >
                  <i
                    class="icon-porta"
                    aria-hidden="true"
                  />
                </div>
                <div class="print-widget-bubble d-flex align-items-center justify-content-center">
                  <span>{{ entityCopy.numCopies }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        <hr>
        <b-row
          v-if="templateForm.template.reportModelsList.includes(ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL)"
          class="mb-2"
        >
          <b-col>
            <span class="type-selector-text dark">{{ $t('grossing.modal.magazineSelect') }} *</span>
            <vt-combo-select
              v-model="magazineSelected"
              v-validate="'required'"
              track-by="reportModelEnum"
              :custom-label="(value) => `${value.printer} - ${value.magazine}`"
              :option-loader="getAllConfiguratedMagazines"
            />
          </b-col>
        </b-row>
      </div>
    </b-container>
    <div class="print-widget-footer">
      <div
        v-if="selectedTemplateId"
        class="preview-label d-none"
      >
        <!-- TODO: Esto es la vista previa de los estudios que abarca la impresión -->
        <span>C20-000051...56</span>
        <!-- TODO: Lo siguiente es para mostrar antes de generar la vista previa de etiquetas -->
        <span class="grey-normal is-italic d-none">{{ $t('component.printLabelsModal.labelPreview') }}</span>
        <!-- TODO: Botón para que el usuario refresque la vista previa si lo ve necesario -->
        <b-button class="px-1 btn__icon btn__icon--primary  px-0">
          <i
            class="ml-2 far fa-sync-alt"
            aria-hidden="true"
          />
        </b-button>
      </div>
      <!-- TODO: Mejorar el contenido del botón, en función del número de etiquetas poner en plural o singular el "tc" -->
      <b-button
        v-if="selectedTemplateId"
        block
        class="btn--primary"
        @click="checkValidationAndPrint(templateForm)"
      >
        {{ $t('component.printLabelsModal.print') }} {{ $tc('component.printLabelsModal.label', 2) }}
      </b-button>
      <b-button
        v-if="!selectedTemplateId"
        block
        class="btn--primary"
        @click="showPrintLabelsModal"
      >
        {{ $t('component.printLabelsModal.goOn') }}
      </b-button>
    </div>
    <print-labels-modal
      ref="printLabelsModal"
      @reloadTemplates="loadTemplates()"
      @printLabels="printLabels($event.templateForm, $event.selectedRows, $event.magazineSelected)"
    />
    <print-component ref="printComponent" />
  </div>
</template>

<script>
import PrintLabelsModal from '@/components/modals/printLabelsModal/PrintLabelsModal'
import batchPrintingDashboardServices, { COREENTITY_PRINT_LABELS_VALUES, ENTITY_LABELS } from '@/api/resources/common/batchPrintingDashboardServices'
import CommonCaseTypeServices from '@/api/resources/common/commonCaseTypeServices'
import PrintComponent from '@/modules/printModule/PrintComponent'
import magazineInformationServices from '@/api/resources/common/magazineInformationServices'

export default {
    name: 'PrintWidget',
    components: {
        PrintLabelsModal,
        PrintComponent
    },
    props: {
        widget: { type: Object, required: true }
    },
    data() {
        return {
            ENTITY_LABELS: ENTITY_LABELS,
            magazineSelected: null,
            selectedTemplateId: null,
            templates: [],
            caseTypes: [],
            selected: null,
            templateForm: {
                description: '',
                template: {
                    caseType: null,
                    entityCopiesRequestList: [
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.CASE,
                            reportModel: ENTITY_LABELS.REGISTRY_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.SAMPLE,
                            reportModel: ENTITY_LABELS.SAMPLE_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.PORTION,
                            reportModel: ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.SUBPORTION,
                            reportModel: ENTITY_LABELS.SUBPORTION_SLIDE_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        }
                    ],
                    hasVirtualPortion: false,
                    reportModelsList: [],
                    maxValue: 0,
                    minValue: 0,
                    numberOfNextLabels: 0,
                    rangeLabel: false,
                    reportConfigId: []
                },
                title: '',
                toBeUsedByDefault: false
            },
            printLabelTemplate: true,
            printButtonCaseActive: false,
            printButtonSampleActive: false,
            printButtonPortionActive: false,
            printButtonSubportionActive: false,
            options: [
                { value: 'a', text: 'Option 1' },
                { value: 'b', text: 'Option 2' },
                { value: 'c', text: 'Option 3' }
            ],
            switchRank: null
        }
    },
    computed: {
        labId() {
            return this.$store.getters.labId
        }
    },
    async mounted() {
        this.updateLoading(true)
        await this.loadTemplates()
        await this.loadCaseTypes()
        this.updateLoading(false)
    },
    methods: {
        async getAllConfiguratedMagazines() {
            return magazineInformationServices.getAllConfiguratedMagazines(this.labId)
        },
        async validate() {
            return this.$validator.validate()
        },
        async checkIfPrintersAreConfigured(reportModelsList, magazineSelected) {
            const devices = []
            for (const reportModel of reportModelsList) {
                const device = await this.$refs.printComponent.getConfiguredDevice({ report: reportModel })
                if (reportModel === ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL) {
                    const deviceForMagazine = device.find((casetteDevice) => casetteDevice.printer.name === magazineSelected.printer)
                    deviceForMagazine.magazine = magazineSelected.magazine
                    if (deviceForMagazine) {
                        devices.push(deviceForMagazine)
                    }
                } else if (device) {
                    devices.push(device)
                }
            }
            return devices
        },
        async checkValidationAndPrint(templateForm) {
            const validation = await this.validate()
            if (!validation) {
                this.showErrorModalGlobal(this.$t('global.incompleteForm'))
                return
            }
            this.printLabels(templateForm)
        },
        async printLabels(templateForm, selectedRows = null, magazineSelected = null) {
            const configuredPrinters = await this.checkIfPrintersAreConfigured(templateForm.template.reportModelsList, magazineSelected || this.magazineSelected)
            if (configuredPrinters.length !== templateForm.template.reportModelsList.length) {
                return
            }
            if (selectedRows && selectedRows.length) {
                const selectedLabelsForPrint = selectedRows.map((row) => {
                    return {
                        entityId: row.entityId,
                        entityType: row.entityType,
                        label: row.label,
                        reportModel: row.reportModel,
                        numCopies: row.numCopies,
                        series: row.series
                    }
                })
                this.updateLoading(true)
                const [err, response] = await this.toCall(batchPrintingDashboardServices.generateSelectedLabelsForPrint(this.labId, selectedLabelsForPrint, templateForm.template.reportModelsList.length))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                await this.sendLabelsToPrinter(response, configuredPrinters)
            } else if (!selectedRows || !selectedRows.length) {
                this.updateLoading(true)
                const [err, response] = await this.toCall(batchPrintingDashboardServices.generateLabelsForPrint(this.labId, templateForm.template))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                await this.sendLabelsToPrinter(response, configuredPrinters)
            }
        },
        async sendLabelsToPrinter(labels, configuredPrinters) {
            for (let label of labels) {
                const device = configuredPrinters.find((printer) => printer.reportModel === label.reportModel)
                let labelPrinted
                if (label.reportModel === ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL) {
                    labelPrinted = await this.$refs.printComponent.sendLabelsToCassettesDevice(label.labelPdf, device, device.magazine)
                } else {
                    labelPrinted = await this.$refs.printComponent.sendLabelsToLabelsDevice(label.labelPdf, device)
                }
                if (!labelPrinted) {
                    break
                }
            }
        },
        entitySelected(entity) {
            entity.checked = !entity.checked
            if (entity.checked) {
                this.templateForm.template.reportModelsList.push(entity.reportModel)
            } else {
                this.templateForm.template.reportModelsList = this.templateForm.template.reportModelsList.filter((label) => label !== entity.reportModel)
            }
            this.$forceUpdate()
        },
        showPrintLabelsModal(editTemplate) {
            const selectedTemplate = editTemplate ? this.templates.find((template) => template.id === this.selectedTemplateId) : null
            this.$refs.printLabelsModal.show(selectedTemplate)
        },
        async loadTemplates(templateSelected = null) {
            const [err, response] = await this.toCall(batchPrintingDashboardServices.getTemplatesByLaboratory(this.labId))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.templates = response
            if (this.templates.length) {
                const defaultTemplate = this.templates.find((template) => template.toBeUsedByDefault)
                if (templateSelected) {
                    this.selectTemplate(templateSelected)
                } else if (defaultTemplate) {
                    this.selectTemplate(defaultTemplate)
                } else {
                    this.selectTemplate(this.templates[0])
                }
            } else {
                this.$_cleanComponentData()
            }
        },
        async loadCaseTypes() {
            const [err, response] = await this.toCall(CommonCaseTypeServices.getActiveCaseTypesByLaboratory(this.labId))
            if (err) {
                this.showErrorModalGlobal()
            }
            this.caseTypes = response
        },
        selectTemplate(template) {
            this.$root.$emit('bv::hide::popover')
            this.selectedTemplateId = template.id
            this.templateForm.title = template.title
            this.templateForm.description = template.description
            this.templateForm.toBeUsedByDefault = template.toBeUsedByDefault
            this.templateForm.template.entityCopiesRequestList = template.batchPrinting.entityCopiesRequestList
            this.templateForm.template.entityCopiesRequestList.forEach((entity) => {
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.CASE) {
                    entity.reportModel = ENTITY_LABELS.REGISTRY_LABEL
                }
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.SAMPLE) {
                    entity.reportModel = ENTITY_LABELS.SAMPLE_LABEL
                }
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.PORTION) {
                    entity.reportModel = ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL
                }
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.SUBPORTION) {
                    entity.reportModel = ENTITY_LABELS.SUBPORTION_SLIDE_LABEL
                }
            })
            this.templateForm.template.caseType = template.batchPrinting.caseType
            this.templateForm.template.hasVirtualPortion = template.batchPrinting.hasVirtualPortion
            this.templateForm.template.maxValue = template.batchPrinting.maxValue
            this.templateForm.template.minValue = template.batchPrinting.minValue
            this.templateForm.template.numberOfNextLabels = template.batchPrinting.numberOfNextLabels
            this.templateForm.template.rangeLabel = template.batchPrinting.rangeLabel
            this.templateForm.template.reportModelsList = template.batchPrinting.reportModelsList
            template.batchPrinting.reportModelsList.forEach((label) => {
                this.templateForm.template.entityCopiesRequestList.find((entity) => entity.reportModel === label).checked = true
            })
        }
    }
}
</script>

<style>
    .print-widget-component .widget-truncate {
        max-width:150px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>
